// Assignment / workbook submission capture + Day 5 grading wiring.
//
// Day 4 scope: reliably CAPTURE answers.
// Day 5 scope: on SUBMIT-FINAL, run the pure grader (src/lib/grading.ts),
//   persist the auto-score + pass/fail/needs-grade status, and expose admin
//   manual-grade helpers.
//
// Model (0001_init.sql + 0003 + 0004 additive migrations):
//   - one `submissions` row per (enrollment_id, lesson_id, content-question-id),
//     with attempt_id = null and question_id = null (assignment questions are
//     file-authored, not in the `questions` table; see src/content/assignments.ts).
//     The content question id is stored inside answer jsonb as { qid, value }.
//   - one `assignment_submission_status` row per (enrollment_id, lesson_id)
//     tracking draft vs submitted AND (Day 5) auto_percent / auto_passed /
//     grade_status / manual_pass / manual_note / graded_at / graded_by.
//
// GRADING IS AUTHORITATIVE SERVER-SIDE (Day 7 GATE 1): on SUBMIT-FINAL this
// module POSTs the student's answers to the Worker /api/grade with the student's
// Supabase session JWT. The Worker holds the answer key (functions/_lib/
// answer-keys.ts), scores it, and writes auto_percent / auto_passed / grade_status
// to assignment_submission_status with the service-role key. The CLIENT no longer
// ships the answer key, does not compute the authoritative score, and does not
// write the grade columns on submit — it only renders what the server returns.
// (Answers themselves are still saved to `submissions` under the student's own
// RLS-scoped session so the instructor can read them and they survive reloads.)

import { supabase } from "./supabase";
import type {
  SubmissionRow,
  SubmissionAnswer,
  AssignmentSubmissionStatusRow,
  AssignmentStatusValue,
  GradeStatusValue,
} from "./types";
import type { AssignmentQuestion } from "../content/assignments";

/** Shape the Worker /api/grade returns on success. */
interface ServerGradeResult {
  autoPercent: number;
  autoPassed: boolean;
  gradeStatus: GradeStatusValue;
  perQuestion: Array<{
    qid: string;
    type: string;
    correct: boolean;
    needsManualGrade: boolean;
  }>;
  needsManualGradeQids: string[];
}

export type AnswerMap = Record<string, string | number | boolean | null>;

const STATUS_SELECT =
  "id, enrollment_id, lesson_id, status, submitted_at, auto_percent, auto_passed, grade_status, manual_pass, manual_note, graded_at, graded_by";

/** Read back a student's prior answers for an assignment lesson. */
export async function fetchAssignmentAnswers(
  enrollmentId: string,
  lessonId: string,
): Promise<AnswerMap> {
  const { data } = await supabase
    .from("submissions")
    .select(
      "id, attempt_id, enrollment_id, lesson_id, question_id, answer, auto_score, manual_score, graded_by, graded_at",
    )
    .eq("enrollment_id", enrollmentId)
    .eq("lesson_id", lessonId)
    .is("attempt_id", null);
  const map: AnswerMap = {};
  for (const row of (data as SubmissionRow[] | null) ?? []) {
    const a = row.answer as SubmissionAnswer | null;
    if (a && a.qid) map[a.qid] = a.value;
  }
  return map;
}

/** Read back the draft/submitted + grade status for an assignment lesson. */
export async function fetchAssignmentStatus(
  enrollmentId: string,
  lessonId: string,
): Promise<AssignmentSubmissionStatusRow | null> {
  const { data } = await supabase
    .from("assignment_submission_status")
    .select(STATUS_SELECT)
    .eq("enrollment_id", enrollmentId)
    .eq("lesson_id", lessonId)
    .maybeSingle();
  return (data as AssignmentSubmissionStatusRow | null) ?? null;
}

/** Statuses for many assignment lessons at once (dashboard badges). */
export async function fetchAssignmentStatuses(
  enrollmentId: string,
  lessonIds: string[],
): Promise<Record<string, AssignmentSubmissionStatusRow>> {
  if (!enrollmentId || lessonIds.length === 0) return {};
  const { data } = await supabase
    .from("assignment_submission_status")
    .select(STATUS_SELECT)
    .eq("enrollment_id", enrollmentId)
    .in("lesson_id", lessonIds);
  const map: Record<string, AssignmentSubmissionStatusRow> = {};
  for (const row of (data as AssignmentSubmissionStatusRow[] | null) ?? []) {
    map[row.lesson_id] = row;
  }
  return map;
}

/** What saveAssignment returns to the caller (so the UI can show the result). */
export interface SaveAssignmentResult {
  error: string | null;
  /** Present only on submit-final: the authoritative auto percent (0..100). */
  autoPercent?: number;
  /** Present only on submit-final: the overall grade status from the server. */
  gradeStatus?: GradeStatusValue;
}

/**
 * POST the student's answers to the authoritative server-side grader. Sends the
 * Supabase session JWT so the Worker can verify the caller and confirm the
 * enrollment is theirs. The Worker holds the answer key, scores it, and writes
 * the grade columns with the service-role key — the client never sees the key
 * and never writes the score. Returns the server's grade, or an error string.
 */
async function gradeOnServer(
  enrollmentId: string,
  lessonId: string,
  answers: AnswerMap,
): Promise<{ error: string | null; result?: ServerGradeResult }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) return { error: "You appear to be signed out. Please sign in again." };

  let res: Response;
  try {
    res = await fetch("/api/grade", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ enrollmentId, lessonId, answers }),
    });
  } catch (e) {
    return { error: e instanceof Error ? e.message : "Could not reach the grader." };
  }

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* ignore parse error; handled below */
  }
  if (!res.ok) {
    const err =
      payload && typeof payload === "object" && "error" in payload
        ? String((payload as { error: unknown }).error)
        : `grade_failed_${res.status}`;
    return { error: err };
  }
  return { error: null, result: payload as ServerGradeResult };
}

/**
 * Persist answers for an assignment lesson.
 * - Replaces prior submission rows for this (enrollment, lesson) and re-inserts
 *   one row per answered question (the raw answers — RLS-scoped to the student).
 * - On save-as-DRAFT: upserts the status row to `draft`; NO grading is performed
 *   (the client never grades — grade columns stay null/pending).
 * - On SUBMIT-FINAL: after saving the answers, calls the AUTHORITATIVE server
 *   grader (/api/grade), which scores with the server-held key and writes
 *   auto_percent / auto_passed / grade_status with the service-role key. The
 *   client renders what the server returns; it does not compute or write a score.
 */
export async function saveAssignment(opts: {
  enrollmentId: string;
  lessonId: string;
  questions: AssignmentQuestion[];
  answers: AnswerMap;
  status: AssignmentStatusValue;
}): Promise<SaveAssignmentResult> {
  const { enrollmentId, lessonId, questions, answers, status } = opts;

  const submitting = status === "submitted";

  // Build submission rows for questions that have a non-empty answer. We no
  // longer stamp auto_score here (the client cannot grade — no answer key); the
  // authoritative per-question result lives on the server. auto_score stays null
  // until/unless a server path backfills it.
  const rows = questions
    .filter((q) => {
      const v = answers[q.id];
      return v !== undefined && v !== null && v !== "";
    })
    .map((q) => ({
      enrollment_id: enrollmentId,
      lesson_id: lessonId,
      attempt_id: null as string | null,
      question_id: null as string | null,
      answer: { qid: q.id, value: answers[q.id] } as SubmissionAnswer,
      auto_score: null as number | null,
    }));

  // Replace prior answers for this assignment to stay consistent.
  const { error: delErr } = await supabase
    .from("submissions")
    .delete()
    .eq("enrollment_id", enrollmentId)
    .eq("lesson_id", lessonId)
    .is("attempt_id", null);
  if (delErr) return { error: delErr.message };

  if (rows.length > 0) {
    const { error: insErr } = await supabase.from("submissions").insert(rows);
    if (insErr) return { error: insErr.message };
  }

  // DRAFT: just record the draft status row (no grading, no server call).
  if (!submitting) {
    const { error: statusErr } = await supabase
      .from("assignment_submission_status")
      .upsert(
        {
          enrollment_id: enrollmentId,
          lesson_id: lessonId,
          status,
          submitted_at: null,
          grade_status: "pending" as GradeStatusValue,
        },
        { onConflict: "enrollment_id,lesson_id" },
      );
    if (statusErr) return { error: statusErr.message };
    return { error: null };
  }

  // SUBMIT-FINAL: hand off to the AUTHORITATIVE server grader. The Worker writes
  // auto_percent / auto_passed / grade_status on assignment_submission_status with
  // the service-role key — the client does NOT write those columns on submit.
  const graded = await gradeOnServer(enrollmentId, lessonId, answers);
  if (graded.error || !graded.result) {
    return { error: graded.error ?? "Grading failed." };
  }

  return {
    error: null,
    autoPercent: graded.result.autoPercent,
    gradeStatus: graded.result.gradeStatus,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Day 5 — Instructor manual grading (Admin area, behind RequireAdmin).
//
// These run under the browser anon key; the admin RLS policies in 0004 allow an
// is_admin student to read every submission/status row and update the manual
// grade columns. (A service-role Worker path would bypass RLS entirely; not
// needed for the build.)
// ─────────────────────────────────────────────────────────────────────────────

/** A submission-status row joined with the lesson/enrollment context the admin needs. */
export interface GradingQueueItem {
  status: AssignmentSubmissionStatusRow;
  lessonTitle: string | null;
  lessonBodyRef: string | null;
  studentName: string | null;
  studentEmail: string | null;
}

/**
 * List submitted assignments that still need instructor manual grading
 * (grade_status = 'needs_manual_grade'). Admin-only (RLS-gated by 0004).
 */
export async function fetchGradingQueue(): Promise<GradingQueueItem[]> {
  const { data, error } = await supabase
    .from("assignment_submission_status")
    .select(
      `${STATUS_SELECT},
       lesson:lessons ( title, body_ref ),
       enrollment:enrollments ( student:students ( full_name, email ) )`,
    )
    .eq("grade_status", "needs_manual_grade")
    .order("submitted_at", { ascending: true });

  if (error) throw new Error(error.message);

  // PostgREST embedded resources may be inferred as object OR array depending on
  // the relationship; normalize to the first element either way.
  type Embedded<T> = T | T[] | null;
  type LessonEmbed = { title: string | null; body_ref: string | null };
  type StudentEmbed = { full_name: string | null; email: string | null };
  type EnrollmentEmbed = { student: Embedded<StudentEmbed> };
  type Joined = AssignmentSubmissionStatusRow & {
    lesson: Embedded<LessonEmbed>;
    enrollment: Embedded<EnrollmentEmbed>;
  };

  const first = <T>(v: Embedded<T>): T | null =>
    Array.isArray(v) ? (v[0] ?? null) : (v ?? null);

  return ((data as unknown as Joined[] | null) ?? []).map((row) => {
    const { lesson, enrollment, ...status } = row;
    const l = first(lesson);
    const enr = first(enrollment);
    const stu = enr ? first(enr.student) : null;
    return {
      status: status as AssignmentSubmissionStatusRow,
      lessonTitle: l?.title ?? null,
      lessonBodyRef: l?.body_ref ?? null,
      studentName: stu?.full_name ?? null,
      studentEmail: stu?.email ?? null,
    };
  });
}

/** Fetch a single submission's answers for the admin to read while grading. */
export async function fetchSubmissionAnswersForAdmin(
  enrollmentId: string,
  lessonId: string,
): Promise<AnswerMap> {
  return fetchAssignmentAnswers(enrollmentId, lessonId);
}

/**
 * Instructor marks the free_response item(s) of a submission complete/incomplete
 * and (optionally) leaves a note. Recomputes the overall grade_status from the
 * stored auto_passed + the instructor's manual verdict:
 *   - auto failed                  → 'failed'
 *   - auto passed + manual complete → 'passed'
 *   - auto passed + manual incomplete → 'needs_manual_grade' (still pending fix)
 * Admin-only (RLS-gated by 0004).
 */
export async function setManualGrade(opts: {
  statusId: string;
  autoPassed: boolean | null;
  manualPass: boolean;
  note?: string | null;
  graderStudentId: string | null;
}): Promise<{ error: string | null; gradeStatus: GradeStatusValue }> {
  const { statusId, autoPassed, manualPass, note, graderStudentId } = opts;

  // Resolve overall status from auto + manual verdict.
  let gradeStatus: GradeStatusValue;
  if (autoPassed === false) {
    gradeStatus = "failed";
  } else if (manualPass) {
    gradeStatus = "passed";
  } else {
    // Auto ok (or unknown) but instructor marked the manual item incomplete.
    gradeStatus = "needs_manual_grade";
  }

  const { error } = await supabase
    .from("assignment_submission_status")
    .update({
      manual_pass: manualPass,
      manual_note: note ?? null,
      grade_status: gradeStatus,
      graded_at: new Date().toISOString(),
      graded_by: graderStudentId,
    })
    .eq("id", statusId);

  return { error: error?.message ?? null, gradeStatus };
}
